import type { ModeDeLaCarte } from '@nextilt/commun';

/*
 * Les mots du Mode d'un projet (chantier modes-de-la-carte, modification 01, décisions d'Igor du
 * 2026-10-21) : le bouton de la tête du projet, ses deux onglets à plat (l'Habit d'aujourd'hui, et
 * le Mode), et ce que dit chaque préréglage des touches de la carte. Ce que fait chaque touche est
 * dans `client/src/projets/modes.ts` ; ici, seulement ses mots.
 */
export const MODE = 'Mode';
export const MODE_DU_PROJET = 'Le mode du projet';
export const MODE_DE_LA_BULLE = 'Le mode de cette bulle';
/** Le choix d'une bulle sans mode à elle : celui de sa mère (deuxième passe de 01 et 02). */
export function COMME_LA_MERE(mere: string): string {
  return `Comme « ${mere} »`;
}
export function PHRASE_COMME_LA_MERE(mode: string): string {
  return `Ses touches, tant que cette bulle n’en choisit pas d’autres : ${mode}.`;
}
export const ONGLET_HABIT = 'Habit';
export const ONGLET_MODE = 'Mode';
export const EXPLICATION_DU_MODE =
  'Les touches de la carte et de la hiérarchie, à l’ordinateur. Ses étapes le prennent, tant qu’elles n’en ont pas un à elles.';
/** Le titre des touches de partout : les modes qui les gardent toutes (Analyse n'en garde que trois). */
export function DANS_LES_MODES(noms: readonly string[]): string {
  if (noms.length <= 1) return `Dans ${noms[0] ?? ''}`;
  return `Dans ${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`;
}
export const MODE_PAS_ECRIT = 'Le mode n’a pas pu s’enregistrer, réessaie';

/** Le nom de chaque mode, et sa phrase : ce qu'il apporte, pour le choisir en connaissance. */
export const MOTS_DES_MODES: Record<ModeDeLaCarte, { nom: string; phrase: string }> = {
  'de-base': {
    nom: 'De base',
    phrase: 'La carte telle qu’elle est : seulement les touches de tous les modes.',
  },
  reflexion: {
    nom: 'Réflexion',
    phrase: 'Pour poser ses idées : un post-it ou une liste d’une touche, et une info à la place des titres.',
  },
  execution: {
    nom: 'Exécution',
    phrase: 'Pour passer à l’action : deux infos ensemble à la place des titres, le numéro sous la main.',
  },
  analyse: {
    nom: 'Analyse',
    phrase: 'Pour lire le projet : un panneau d’analyse, et les bulles regroupées.',
  },
};

/** La touche Échap, telle que l'écran l'écrit. */
export const TOUCHE_ECHAP = 'Échap';
/** La touche Ctrl, seule : elle ouvre le panneau Mode. */
export const TOUCHE_CTRL = 'Ctrl';

/** Les marges qu'ouvrent M, R, F. */
export const MARGE_AVANCEMENT = 'Comment j’avance';
export const MARGE_REPERTOIRE = 'Répertoire du projet';
export const MARGE_FRISE = 'La frise des dates';
export const AUJ = 'auj.';
export const PERSONNE_DANS_LE_PROJET = 'Aucun contact dans ce projet.';
export const RIEN_DE_DATE_A_VENIR = 'Rien de daté à venir.';
/** Au bout de la frise, les dates au-delà des quatre prochaines semaines. */
export function plusLoinDansLaFrise(n: number): string {
  return `+ ${n} plus loin`;
}
export const FRISE_REPLIER = 'Les quatre prochaines semaines';
export const RIEN_A_AVANCER = 'Aucune étape ici.';
/** « 2/5 » : le compte d'une étape, comme la maquette. Jamais de pourcentage. */
export function compteDeLEtape(faites: number, total: number): string {
  return `${faites}/${total}`;
}

/** Ce que fait chaque touche, dans le panneau Mode. */
export const OUVRE_L_AVANCEMENT = 'ouvre « Comment j’avance », à gauche';
export const OUVRE_LE_REPERTOIRE = 'ouvre le répertoire du projet, à droite';
export const OUVRE_LA_FRISE = 'ouvre la frise des dates, en haut';
export const HIERARCHISE = 'hiérarchise';
/** J (chantier liens-entre-bulles, ticket 04) : lance « Lier », sur la Carte. */
export const LIE_DES_BULLES = 'relie des bulles';
/** A (chantier liens-entre-bulles, ticket 03) : une bascule, la même touche recache. */
export const MONTRE_LES_LIENS_CACHES = 'montre les liens cachés, puis les recache';
export const CHERCHE_DANS_LE_PROJET = 'cherche dans le projet';
export const REMET_TOUT = 'remet tout comme avant';
export const OUVRE_LE_PANNEAU_MODE = 'ouvre ce panneau, seule';
export const POSE_UN_POST_IT = 'pose un post-it';
export const POSE_UNE_LISTE = 'pose une liste';
export const REGROUPE_LES_BULLES = 'regroupe les bulles';
export const OUVRE_LE_PANNEAU_D_ANALYSE = 'ouvre le panneau d’analyse, à gauche';

/*
 * Les mots du panneau d'analyse et de Regrouper (chantier theme-analyse, ticket 01) : les cinq
 * états d'une bulle en Analyse, et le nom des groupes sans contact, sans lieu, sans date.
 */
export const MOTS_DES_ETATS_D_ANALYSE = {
  a_faire: 'À faire',
  monde: 'J’attends le monde',
  bloquee: 'Bloquée',
  acquise: 'Acquise',
  reflexion: 'En réflexion',
} as const;
export const GROUPE_SANS_CONTACT = 'Personne';
export const GROUPE_SANS_LIEU = 'Nulle part';
export const GROUPE_SANS_DATE = 'Sans date';

/*
 * Le panneau d'analyse (chantier theme-analyse, ticket 02 ; maquette `quatre-themes.html`,
 * `panneauAnalyse`) : ses blocs, et ce que disent les bulles en Analyse.
 */
export const PANNEAU_D_ANALYSE = 'Le panneau d’analyse';
export const AVANCEMENT = 'Avancement';
export function surLeTotal(total: number): string {
  return `sur ${total}`;
}
export const RESTANTES = 'restantes';
export const SANS_DUREE = 'sans durée';
export const ECHEANCES_QUI_APPROCHENT = 'Échéances qui approchent';
export const OU_PASSE_LE_TEMPS = 'Où passe le temps';
export const AUCUNE_DUREE_REGLEE = 'Aucune durée réglée.';
export const REGROUPER_PAR = 'Regrouper par';
/** Les choix de « Regrouper par », dans l'ordre où G les fait tourner (le geste : ticket 03). */
export const MOTS_DES_REGROUPEMENTS = {
  rien: 'rien',
  etat: 'état',
  contact: 'contact',
  lieu: 'lieu',
  mois: 'mois',
} as const;
/*
 * Regrouper (ticket 03 ; maquette `regroupe`, `.titre-g`, le panneau quand on regroupe) : le titre
 * d'une colonne, et ce que dit le panneau d'un groupe.
 */
/** « À FAIRE · 3 » : le titre d'une colonne, en capitales, et combien de bulles elle porte. */
export function titreDeColonne(nom: string, combien: number): string {
  return `${nom.toLocaleUpperCase('fr')} · ${combien}`;
}
/** « Par état », le titre des blocs d'un regroupement. */
export function parRegroupement(par: Exclude<keyof typeof MOTS_DES_REGROUPEMENTS, 'rien'>): string {
  return `Par ${MOTS_DES_REGROUPEMENTS[par]}`;
}
/** « Où passe le temps, par état » : le temps comparé entre groupes. */
export function ouPasseLeTempsPar(par: Exclude<keyof typeof MOTS_DES_REGROUPEMENTS, 'rien'>): string {
  return `${OU_PASSE_LE_TEMPS}, par ${MOTS_DES_REGROUPEMENTS[par]}`;
}
/** « 3 bulles », « 1 bulle » : ce que porte un groupe. */
export function nBullesDuGroupe(n: number): string {
  return `${n} bulle${n > 1 ? 's' : ''}`;
}

/**
 * Le compte à rebours d'une date : « J-4 », « J-0 » le jour même, « depuis 3 j » une fois passée.
 * Jamais « en retard » (principe 3).
 */
export function compteARebours(dans: number): string {
  return dans < 0 ? `depuis ${-dans} j` : `J-${dans}`;
}
/** Le temps qui reste dans la feuille, sous le titre du centre : « ≈ 4 h ». */
export function environ(duree: string): string {
  return `≈ ${duree}`;
}

/** La recherche de la carte (/), la barre de la maquette. */
export const CHERCHER_DANS_LA_CARTE = 'Chercher : un titre, une note, un lieu, un contact…';
export const CHERCHER = 'Chercher dans le projet';

/** Ce qu'une touche d'info peut montrer à la place du titre d'une bulle. */
export const MOTS_DES_INFOS = {
  contact: 'le contact',
  contactEtNumero: 'le contact, son numéro',
  date: 'la date',
  lieu: 'le lieu',
  duree: 'le temps',
  echeance: 'l’échéance',
} as const;

/** « D · la date », « C · le contact, son numéro, et le lieu » : les infos d'une touche, dites. */
export function infosEnMots(infos: readonly (keyof typeof MOTS_DES_INFOS)[]): string {
  const mots = infos.map((i) => MOTS_DES_INFOS[i]);
  if (mots.length <= 1) return mots[0] ?? '';
  return `${mots.slice(0, -1).join(', ')}, et ${mots[mots.length - 1]}`;
}
